<template>
  <div>
    <InvoiceMasterModal
      v-if="!loading && masterModal"
      ref="master-modal"
      :invoice-id="parseInt(invoiceId)"
      :stage="masterModal"
      @close="closeInvoicesModal"
    />
    <portal to="dash-modal">
      <InvoiceCopyModal 
        v-if="invoiceToCopy"
        :invoice="invoiceToCopy"
      />

      <RecurrentInvoiceModal 
        v-if="showScheduler"
        @updated-invoice="refreshLists"
      />
    </portal>
    <InvoiceUploadModal v-if="!loading && showUploadModal && modalType === 'upload'" @close="closeInvoicesModal" />
    <TitleBar>
      <template v-slot:title>{{ $gettext('Invoices') }}</template>
      <template v-slot:buttons>
        <GekkoButton 
          v-if="!isControlPanel" 
          :to="{ name: 'InvoiceAdd' }"
          :text="$gettext('New')"
          icon="fa-solid fa-plus"
          class="mr-2"
        />

        <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown full-btn-toggle mr-2" variant="outline-primary" right no-caret>
          <template #button-content>
            <font-awesome-icon icon="fa-solid fa-plus" class="mr-1" />
            <span v-translate>Upload</span>
          </template>
          <b-dropdown-item :to="{ name: 'InvoiceUpload' }">
            <font-awesome-icon icon="fa-regular fa-file-lines" />
            <span v-translate>Invoice</span>
          </b-dropdown-item>
          <b-dropdown-item :to="{ name: 'InvoiceUploadCorrection' }">
            <font-awesome-icon icon="fa-regular fa-file-exclamation" />
            <span v-translate>Correction</span>
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" variant="outline-primary" right no-caret>
          <template #button-content>
            <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
          </template>
          <b-dropdown-item @click="downloadData('csv')">
            <font-awesome-icon icon="fa-solid fa-download" />
            <span v-translate>Download as .xls</span>
          </b-dropdown-item>
          <b-dropdown-item @click="downloadData('zip')">
            <font-awesome-icon icon="fa-solid fa-download" />
            <span v-translate>Download as .xls + PDFs</span>
          </b-dropdown-item>
          <b-dropdown-item v-if="!isControlPanel" :href="getUrl('accounts_brands_settings')">
            <font-awesome-icon icon="fa-solid fa-paintbrush" />
            <span v-translate>Design your invoice</span>
          </b-dropdown-item>
          <b-dropdown-item v-if="!isControlPanel" :href="getUrl('accounts_invoice_settings')">
            <font-awesome-icon icon="fa-solid fa-gear" />
            <span v-translate>Invoice settings</span>
          </b-dropdown-item>
          <b-dropdown-item v-if="!isControlPanel" :href="getUrl('products:home')">
            <font-awesome-icon icon="fa-solid fa-basket-shopping" />
            <span v-translate>Product settings</span>
          </b-dropdown-item>
          <b-dropdown-item v-if="!isControlPanel" :href="getUrl('accounts_ideal_payment_settings')">
            <font-awesome-icon icon="fa-regular fa-credit-card" />
            <span v-translate>iDEAL payments</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </TitleBar>
    <div class="content">
      <InvoicesTable v-if="!loading && !isControlPanel" ref="invoices-table" />
      <AccountantInvoiceTable v-if="!loading && isControlPanel" ref="simple-invoices-table"/>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import invoices from "@/mixins/invoices";
import InvoiceService from "@/store/services/invoice.service.js"
import TitleBar from "@/components/TitleBar"
import GekkoButton from "@/components/GekkoButton"

import InvoiceMasterModal from "@/components/invoices/ui/InvoiceMasterModal"

import InvoiceUploadModal from "@/components/invoices/ui/InvoiceUploadModal"
import InvoiceCopyModal from "@/components/invoices/ui/InvoiceCopyModal"
import RecurrentInvoiceModal from "@/components/invoices/ui/RecurrentInvoiceModal"

import InvoicesTable from "@/components/invoices/InvoicesTable"
import AccountantInvoiceTable from "@/components/accountants/control_panel/AccountantInvoiceTable"

export default {
  name: "Invoices",
  mixins: [invoices],
  components: {
    TitleBar,
    GekkoButton,
    InvoicesTable,
    InvoiceUploadModal,
    InvoiceMasterModal,
    InvoiceCopyModal,
    AccountantInvoiceTable,
    RecurrentInvoiceModal
  },
  data () {
    return {
      masterModal: null,
      showUploadModal: false,
      modalType: null,
      invoiceId: null,
      loading: true
    }
  },
  computed: {
    ...mapGetters({ 
      invoiceToCopy: 'invoices/getInvoiceToCopy',
      showScheduler: 'invoicesRecurrent/getShowModal'
    }),
    encodedFilters () {
      return this.encodeFilterData()
    },
    isControlPanel () {
      return this.$route.path.includes('control-panel')
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler (to) {
        this.masterModal = to.meta.masterModal
        this.showUploadModal = to.meta.showUploadModal
        this.modalType = to.meta.modalType
        this.invoiceId = this.$route.params['invoiceId']

        if (this.masterModal) {
          document.body.classList.add('master-modal-on')
        } else {
          document.body.classList.remove('master-modal-on')
        }
      }
    },
  },
  created () {
    this.loadInvoicesGlobalData().then(() => {
      this.loading = false
    })
  },
  methods: {
    ...mapActions ({ 
      loadInvoicesGlobalData: "invoices/loadInvoicesGlobalData"
    }),
    encodeFilterData () {
      const filters = this.$store.getters["filterStore/getFilter"]
      const str = [];
      for (var prop in filters) {
        if (Object.prototype.hasOwnProperty.call(filters, prop)) {
          str.push(encodeURIComponent(prop) + "=" + encodeURIComponent(filters[prop]));
        }
      }
      return str.join("&");
    },
    reloadFilters () {
      console.log('reloadFilters')
      if (this.$refs['invoices-table']) {
        this.$refs['invoices-table'].reloadFilterStore()
      }
    },
    refreshLists () {
      this.$store.commit('invoices/REFRESH_INVOICE_RECURRENT_LIST')
      this.$store.commit('invoices/REFRESH_INVOICE_LIST')
    },
    closeInvoicesModal () {
      this.showUploadModal = false
      this.masterModal = null
      this.modalType = null
      this.reloadFilters()
      this.$router.push({ name: 'Invoices' }).catch(() => {})
    },
    isDownloadButtonDisabled(){
      return this.$cookies.get("disable_download_csv_button") === "true";
    },
    // TODO this should be handled by backend via API
    downloadData(export_type){
      if(this.isDownloadButtonDisabled()){
        this.$store.commit('setAlert', {
          title: this.$gettext("Invoice Download"),
          text: this.$gettext("For security reasons, you cannot initiate another download for 2 minutes."),
          variant: "info",
          duration: 4
        });
        return;
      }
      var expiry_date = new Date();
      expiry_date.setSeconds(expiry_date.getSeconds() + 2*60);
      this.$cookies.set("disable_download_csv_button", true, expiry_date);
      
      let url;
      if (export_type === 'csv') {
        url = `${window.Urls['invoice-export-invoices']()}?company_profile=${window.company_id}&${this.encodedFilters}`;
        window.location.href = url;
      }
      else {
        let params = this.$store.getters["filterStore/getFilter"]
        params['company_profile'] = window.company_id
        InvoiceService.exportZip(params).then((response) => {
          if (response.status === 204) {
            this.$store.commit('setAlert', {
              title: this.$gettext("Invoice Download"),
              text: this.$gettext("There are no Sent or Paid invoices to export for this selected filters."),
              variant: "info",
              duration: 4
            })
          } else {
            this.$store.commit('setAlert', {
              title: this.$gettext("Invoice Download"),
              text: this.$gettext("We will send you an email shortly with the download link."),
              variant: "info",
              duration: 4
            })
          }
        }).catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext("Invoice Download Error"),
            text: this.$gettext("An error occurred while attempting to download your invoice list."),
            variant: "danger",
            duration: 4
          })
        })
      }
    },
    openCorrectionModal() {
      this.$refs['accountant-correction-modal'].show()
    }
  }
}
</script>
<style lang="scss">
  .invoice-wrap {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 2rem 1px; // extra px for box shadow
    min-height: 100%;
    width: 100%;
  }
  .invoice-wrap-bg {
    padding: 0 1rem;
    @media (min-width: '992px') {
      min-height: 100%;
      .invoice-wrap {
        min-height: 100vh;
        max-width: 1200px;
        margin: 0 auto;
        background: white;
      }
    }
  }

  .invoice-section {
    display: flex;
    flex-direction: column;
    &--label {
      font-size: 1rem;
      margin-bottom: 0.5rem;
    }
    .invoice-lines-wrap {
      overflow-x: auto;
      overflow-y: visible;
      .gk-table {
        background: transparent;
      }
    }
    &--lines {
      padding: 1px; // extra for table shadow
      border-radius: $radiusLg;
      .gk-table-invoicelines thead td:first-child {
        padding-left: 0.35rem;
        padding-right: 0.35rem;
      }
    }
  }
</style>
